import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatoCantidad } from '@/lib/utils/formato-precio';

export interface OpcionEnPildora<T extends string> {
  valor: T;
  texto: string;
  /** El número al lado («Todas 9»). null o ausente = no se muestra (no se sabe, no es 0). */
  cuenta?: number | null;
  /** Sólo para `Segmentado`: un ícono decorativo antes del texto. */
  icono?: LucideIcon;
}

interface PropsDelGrupo<T extends string> {
  opciones: Array<OpcionEnPildora<T>>;
  valor: T;
  onCambiar: (v: T) => void;
  /** Qué se elige: el lector de pantalla anuncia el grupo con este nombre. */
  'aria-label': string;
  className?: string;
}

const FOCO =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

/**
 * Los filtros en píldoras de la maqueta («Todas 9 · Todo el catálogo 9 · Por
 * marca 0»): la elegida rellena con la tinta del texto, las otras con borde.
 *
 * Es la misma API que `GrupoDeOpciones` (los filtros de Reglas y Estrategias
 * ya la usaban), con la cara de la maqueta. A diferencia de `ChipsDeFiltro`,
 * deja un espacio entre el texto y el número: así el nombre accesible es «Todas
 * 9» y no «Todas9» (el lector de pantalla y los tests lo leen igual que se ve).
 */
export function Pildoras<T extends string>({
  opciones,
  valor,
  onCambiar,
  'aria-label': etiqueta,
  className,
}: PropsDelGrupo<T>) {
  return (
    <div role="group" aria-label={etiqueta} className={cn('flex flex-wrap gap-2', className)}>
      {opciones.map((o) => {
        const activa = o.valor === valor;
        return (
          <button
            key={o.valor}
            type="button"
            aria-pressed={activa}
            onClick={() => onCambiar(o.valor)}
            className={cn(
              'inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-sm font-semibold transition-colors',
              FOCO,
              activa
                ? 'border-foreground bg-foreground text-background'
                : 'border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
          >
            {o.texto}
            {o.cuenta != null && ' '}
            {o.cuenta != null && (
              <span
                className={cn(
                  'tabular-nums font-medium',
                  activa ? 'text-background/75' : 'text-muted-foreground'
                )}
              >
                {formatoCantidad(o.cuenta)}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Un selector de «cómo ver» (Tarjetas · Lista · Por fechas): botones pegados
 * dentro de una cápsula, el elegido levantado como una tarjeta. Cada uno lleva
 * `aria-pressed`, igual que `GrupoDeOpciones`.
 */
export function Segmentado<T extends string>({
  opciones,
  valor,
  onCambiar,
  'aria-label': etiqueta,
  className,
}: PropsDelGrupo<T>) {
  return (
    <div
      role="group"
      aria-label={etiqueta}
      className={cn(
        'inline-flex items-center gap-0.5 rounded-full border border-border bg-muted/60 p-0.5',
        className
      )}
    >
      {opciones.map((o) => {
        const activa = o.valor === valor;
        const Icono = o.icono;
        return (
          <button
            key={o.valor}
            type="button"
            aria-pressed={activa}
            onClick={() => onCambiar(o.valor)}
            className={cn(
              'inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 text-sm font-semibold transition-colors max-md:min-h-[40px]',
              FOCO,
              activa
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {Icono && <Icono className="h-3.5 w-3.5" aria-hidden="true" />}
            {o.texto}
          </button>
        );
      })}
    </div>
  );
}
